import { first, on, text } from '../core/dom.js';
import { writeCookie } from '../core/cookies.js';

/**
 * Account, review and campaign-page events (spec §6) — the browser half.
 *
 * Login, password reset, review submission and the birthday form are all reported from
 * PHP, where the outcome is actually known. This file deliberately emits only the two
 * events that have no server-side signal at all.
 *
 * It used to also emit `password_reset_requested` and `site_search_performed`, both of
 * which PHP already sends. Neither copy could be collapsed downstream, because the server
 * keys them on a salted hash of the login and on an md5 the browser cannot reproduce — so
 * every reset request and every search was counted twice. The browser now contributes the
 * one thing PHP cannot see, the entry point, and leaves the event itself to the server.
 */

export const RESET_ENTRY_COOKIE = '_td_reset_entry';

const RESET_ENTRY_TTL_SECONDS = 30 * 60;

export function collectAccount(tracker, { doc = document, selectors = {}, win = window } = {}) {
  collectPasswordResetEntry(tracker, doc, selectors);
  collectPersonalization(tracker, doc, selectors, win);
}

/**
 * /moje-konto/lost-password/ is the fourth largest page on the PL site — 58% of every
 * account visitor lands on it — so knowing where they came from is the whole point.
 *
 * Written to a cookie rather than sessionStorage because the consumer is PHP: the reset is
 * a form POST, and `retrieve_password` is where the event is raised.
 */
export function collectPasswordResetEntry(tracker, doc = document, selectors = {}) {
  const remember = () => {
    try {
      writeCookie(RESET_ENTRY_COOKIE, entryPoint(doc), RESET_ENTRY_TTL_SECONDS, doc);
    } catch (e) {
      /* cookie-blocked — the server event simply reports an unknown entry point */
    }
  };

  on(doc, 'click', 'a[href*="lost-password"], a[href*="lost_password"]', remember);

  // Arriving straight on the reset page (from an e-mail, or a bookmark) is itself an entry
  // point worth recording, and there is no click to hang it on.
  if (first(selectors.lostPasswordForm, doc)) {
    remember();
  }
}

/**
 * /personalized/ reaches 1,098 visitors a month and has no funnel of its own. Both events
 * are browser-only: the flow is a client-side quiz with no server round trip that marks
 * either boundary.
 */
export function collectPersonalization(tracker, doc = document, selectors = {}, win = window) {
  const path = tracker.context.page_path || '';

  if (!/personalized|personaliz/i.test(path)) {
    return;
  }

  let started = false;

  ['change', 'click'].forEach((type) => {
    doc.addEventListener(type, (event) => {
      if (started || !event.target.closest('form, button, input, select')) {
        return;
      }
      started = true;

      tracker.track(
        'personalization_started',
        { entry_point: entryPoint(doc) },
        { once: 'session', dedupeKey: 'personalization_started' }
      );
    });
  });

  // The recommendation block is rendered by the flow itself, after an AJAX round trip —
  // it is almost never in the DOM at boot, so checking once here reported nothing. Watch
  // for it instead, and stop watching the moment it arrives.
  let reported = false;

  const report = (result) => {
    if (reported || !result) {
      return false;
    }
    reported = true;

    tracker.track(
      'personalization_completed',
      {
        recommended_skus: recommendedSkus(result),
        segment: result.getAttribute('data-segment') || text(result.querySelector('h1, h2'), 60),
      },
      { once: 'session', dedupeKey: 'personalization_completed' }
    );

    return true;
  };

  if (report(first(selectors.personalizationComplete, doc))) {
    return;
  }

  if (typeof win.MutationObserver !== 'function') {
    return;
  }

  const observer = new win.MutationObserver(() => {
    if (report(first(selectors.personalizationComplete, doc))) {
      observer.disconnect();
    }
  });

  observer.observe(doc.body || doc.documentElement, { childList: true, subtree: true });
}

function recommendedSkus(root) {
  return Array.prototype.slice
    .call(root.querySelectorAll('[data-sku], [data-product-sku]'))
    .map((el) => el.getAttribute('data-sku') || el.getAttribute('data-product-sku'))
    .filter(Boolean)
    .slice(0, 10);
}

function entryPoint(doc) {
  const referrer = doc.referrer || '';

  if (!referrer) {
    return 'direct';
  }

  try {
    const url = new URL(referrer);

    if (url.host !== doc.location.host) {
      return 'external';
    }

    return url.pathname;
  } catch (e) {
    return 'unknown';
  }
}
